<template>
  <div>
    <h2>Credit Card Information</h2>

    <!-- Error alert - persists until dismissed (no auto-dismiss for errors) -->
    <transition name="fade">
      <div class="alert alert-danger d-flex align-items-center" v-if="error">
        <div class="flex-grow-1">{{ errorMessage }}</div>
        <button type="button" class="btn-close ms-2" aria-label="Close" @click="dismissError"></button>
      </div>
    </transition>

    <!-- Success alert - auto-dismisses after 6 seconds with manual dismiss option -->
    <transition name="fade">
      <div class="alert alert-success d-flex align-items-center" v-if="successMessage">
        <div class="flex-grow-1">{{ successMessage }}</div>
        <button type="button" class="btn-close ms-2" aria-label="Close" @click="dismissSuccess"></button>
      </div>
    </transition>

    <div class="alert alert-warning" v-if="allCardsExpired">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="icon alert-icon"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        stroke-width="2"
        stroke="currentColor"
        fill="none"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
        <path d="M12 9v2m0 4v.01"></path>
        <path d="M5 19h14a2 2 0 0 0 1.84 -2.75l-7.1 -12.25a2 2 0 0 0 -3.5 0l-7.1 12.25a2 2 0 0 0 1.75 2.75"></path>
      </svg>
      <div>
        All your payment methods have expired. Please add a new payment method to continue.
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div>
            <button class="btn btn-primary" v-b-modal.update-payment-modal>
              Add Payment Method
            </button>
          </div>
          <div class="text-muted small">
            <span class="me-2">Select a card to set as default payment method</span>
          </div>
        </div>
        <div class="list-group list-group-flush">
          <div
            class="list-group-item bg-white"
            v-for="card in activePaymentMethods"
            :key="`stored-card-${card.paymentProfileId}`"
          >
            <div class="row align-items-center">
              <div class="col-auto">
                <input
                  type="radio"
                  :id="`card-default-${card.paymentProfileId}`"
                  name="default-card"
                  :value="card.paymentProfileId"
                  :checked="card.default"
                  :disabled="isExpired(card) || settingDefault || checkingSubscriptions"
                  @change="setAsDefault(card)"
                  class="form-check-input me-2"
                  :class="{ 'cursor-not-allowed': isExpired(card) }"
                  style="cursor: pointer"
                  :title="
                    isExpired(card)
                      ? 'Cannot set expired card as default'
                      : 'Set as default payment method'
                  "
                />
              </div>
              <div class="col-auto">
                <a href="#">
                  <span
                    v-if="card.cardType"
                    :class="[
                      'payment',
                      'payment-dark',
                      `payment-provider-${card.cardType.toLowerCase()}-dark`,
                      'payment-xs',
                      'me-2',
                    ]"
                  ></span>
                </a>
              </div>
              <div class="col text-truncate">
                <span class="text-reset d-block">
                  {{ splitWords(card.cardType) }} ending in
                  {{ card.cardNumber.slice(-4) }}
                </span>
                <div
                  :class="[
                    'd-block',
                    'text-truncate',
                    'mt-n1',
                    isExpired(card) ? 'text-danger' : 'text-muted',
                  ]"
                >
                  <span v-if="isExpired(card)">
                    <strong>⚠️ Expired {{ card.expirationDate }}</strong>
                  </span>
                  <span v-else> Expires {{ card.expirationDate }} </span>
                </div>
              </div>
              <div class="col-auto">
                <div class="d-flex align-items-center">
                  <span
                    v-if="card.default"
                    class="badge bg-blue me-2"
                    title="Default payment method"
                    v-b-tooltip.hover
                  >
                    Default
                  </span>
                  <!-- Update subscriptions button - shown on default card when subscriptions exist -->
                  <button
                    v-if="card.default && hasSubscriptions"
                    class="btn btn-outline-primary btn-sm me-2"
                    @click="updateSubscriptionsOnly(card)"
                    :disabled="updatingSubscriptions"
                    title="Update all active subscriptions to use this card"
                    v-b-tooltip.hover
                  >
                    <span v-if="updatingSubscriptions" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    <span v-else>
                      <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-sm me-1" width="16" height="16" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4"></path>
                        <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4"></path>
                      </svg>
                    </span>
                    Update subscriptions
                  </button>
                  <a
                    href="#"
                    @click.prevent="confirmDelete(card)"
                    class="list-group-item-actions"
                    title="Delete payment card"
                    v-b-tooltip.hover
                    :class="{ disabled: deleting || activePaymentMethods.length === 1 }"
                  >
                    <!-- Download SVG icon from http://tabler-icons.io/i/trash -->
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="icon text-danger"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                      stroke="currentColor"
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                      <line x1="4" y1="7" x2="20" y2="7"></line>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                      <path
                        d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"
                      ></path>
                      <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <add-card-modal @saved="reloadCards"></add-card-modal>

    <!-- Delete Confirmation Modal -->
    <b-modal
      id="delete-card-modal"
      title="Delete Payment Method"
      @ok="deleteCard"
      ok-title="Delete"
      ok-variant="danger"
      cancel-title="Cancel"
    >
      <p v-if="cardToDelete">
        Are you sure you want to delete the
        {{ splitWords(cardToDelete.cardType) }} ending in
        {{ cardToDelete.cardNumber.slice(-4) }}?
      </p>
      <p class="text-muted mb-0">This action cannot be undone.</p>
    </b-modal>

    <!-- Change Default Card Modal (shown when user has subscriptions) -->
    <b-modal
      id="change-default-modal"
      title="Update Payment Method"
      hide-footer
      @hidden="pendingDefaultCard = null"
    >
      <div v-if="pendingDefaultCard">
        <p>
          You're about to set the 
          <strong>{{ splitWords(pendingDefaultCard.cardType) }} ending in {{ pendingDefaultCard.cardNumber.slice(-4) }}</strong>
          as your default payment method.
        </p>
        <div class="alert alert-info d-flex align-items-start">
          <svg xmlns="http://www.w3.org/2000/svg" class="flex-shrink-0 me-2" width="20" height="20" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round" style="margin-top: 2px;">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
            <circle cx="12" cy="12" r="9"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <div>
            <strong>You have active subscriptions.</strong> Would you also like to update your subscriptions to use this card?
          </div>
        </div>
        <div class="d-flex flex-column gap-2 mt-4">
          <button 
            class="btn btn-primary" 
            @click="handleChangeDefaultConfirm(true)"
            :disabled="settingDefault"
          >
            <span v-if="settingDefault" class="spinner-border spinner-border-sm me-2" role="status"></span>
            Yes, update default and all subscriptions
          </button>
          <button 
            class="btn btn-outline-secondary" 
            @click="handleChangeDefaultConfirm(false)"
            :disabled="settingDefault"
          >
            <span v-if="settingDefault" class="spinner-border spinner-border-sm me-2" role="status"></span>
            No, just update the default
          </button>
          <button 
            class="btn btn-link text-muted" 
            @click="$bvModal.hide('change-default-modal')"
            :disabled="settingDefault"
          >
            Cancel
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<style scoped>
.form-check-input:disabled {
  opacity: 0.5;
  cursor: not-allowed !important;
}

.form-check-input {
  width: 1.25rem;
  height: 1.25rem;
  margin-top: 0;
}

.form-check-input:checked {
  background-color: #206bc4;
  border-color: #206bc4;
}

.cursor-not-allowed {
  cursor: not-allowed !important;
}

/* Modal header styling */
::v-deep .modal-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

/* Close button styling */
::v-deep .modal-header .close,
::v-deep .modal-header .btn-close {
  padding: 0.5rem;
  margin: -0.5rem -0.5rem -0.5rem auto;
  background: transparent;
  border: none;
  font-size: 1.5rem;
  font-weight: 300;
  line-height: 1;
  color: #6c757d;
  opacity: 0.5;
  cursor: pointer;
  transition: opacity 0.15s ease-in-out;
}

::v-deep .modal-header .close:hover,
::v-deep .modal-header .btn-close:hover {
  opacity: 1;
  color: #000;
}

::v-deep .modal-header .close:focus,
::v-deep .modal-header .btn-close:focus {
  outline: none;
  box-shadow: none;
}

/* Fade transition for alerts */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Alert styling - slightly reduced height */
.alert {
  padding: 0.65rem 1rem;
  margin-bottom: 1rem;
}

.alert .btn-close {
  padding: 0.5rem;
  font-size: 0.75rem;
}

/* Update subscriptions button styling */
.btn-outline-primary.btn-sm .icon-sm {
  width: 14px;
  height: 14px;
  vertical-align: -2px;
}
</style>

<script>
import BillingService from "../../../services/BillingService";
import AddCardModal from "./AddCardModal.vue";
import TrackingService from "../../../services/TrackingService";

export default {
  name: "account.billing.billing",
  components: {
    AddCardModal,
  },
  data() {
    return {
      paymentMethods: [],
      cardToDelete: null,
      deleting: false,
      settingDefault: false,
      updatingSubscriptions: false,
      error: false,
      errorMessage: "",
      successMessage: "",
      // For subscription-aware default change
      pendingDefaultCard: null,
      hasSubscriptions: false,
      checkingSubscriptions: false,
    };
  },
  methods: {
    dismissError() {
      this.error = false;
      this.errorMessage = "";
    },
    dismissSuccess() {
      this.successMessage = "";
    },
    splitWords(word) {
      if (word) {
        return word.split(/(?=[A-Z\s]+)/).join(" ");
      } else {
        return "";
      }
    },
    async reloadCards() {
      try {
        this.paymentMethods = await BillingService.getPaymentMethods(
          this.$auth.activeCompany
        );
      } catch (error) {
        this.error = true;
        // Extract the actual error message from the backend response
        if (error.response && error.response.data && error.response.data.message) {
          this.errorMessage = error.response.data.message;
        } else if (error.message) {
          this.errorMessage = error.message;
        } else {
          this.errorMessage = "Failed to load payment methods. Please try again.";
        }
      }
    },
    confirmDelete(card) {
      // Check total cards (including expired ones) for validation
      if (this.paymentMethods.length === 1) {
        this.error = true;
        this.errorMessage =
          "Cannot delete the last payment method. Please add another card first.";
        // Error messages persist until manually dismissed (per UX best practice)
        return;
      }

      this.cardToDelete = card;
      this.$bvModal.show("delete-card-modal");
    },
    async deleteCard() {
      if (!this.cardToDelete) return;

      this.deleting = true;
      this.error = false;
      this.errorMessage = "";
      this.successMessage = "";

      try {
        const result = await BillingService.deletePaymentMethod(
          this.$auth.activeCompany,
          this.cardToDelete.paymentProfileId
        );

        if (result.success) {
          this.successMessage = "Payment method removed";
          TrackingService.track("deleted a saved card");
          await this.reloadCards();

          setTimeout(() => {
            this.successMessage = "";
          }, 6000);
        } else {
          throw new Error(result.message || "Failed to delete payment method");
        }
      } catch (error) {
        this.error = true;
        // Extract the actual error message from the backend response
        // Axios puts the server response in error.response.data
        if (error.response && error.response.data && error.response.data.message) {
          this.errorMessage = error.response.data.message;
        } else if (error.message) {
          this.errorMessage = error.message;
        } else {
          this.errorMessage = "Failed to delete payment method. Please try again.";
        }
      } finally {
        this.deleting = false;
        this.cardToDelete = null;
      }
    },
    isExpired(card) {
      if (!card.expirationDate) return false;

      try {
        // Parse expiration date in format "YYYY-MM" or "MM/YYYY"
        let month, year;

        if (card.expirationDate.includes("-")) {
          // Format: YYYY-MM
          const parts = card.expirationDate.split("-");
          year = parseInt(parts[0]);
          month = parseInt(parts[1]);
        } else if (card.expirationDate.includes("/")) {
          // Format: MM/YYYY
          const parts = card.expirationDate.split("/");
          month = parseInt(parts[0]);
          year = parseInt(parts[1]);
        } else {
          return false;
        }

        // Create date for last day of expiration month
        const expirationDate = new Date(year, month, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return expirationDate < today;
      } catch (error) {
        console.error("Error parsing expiration date:", error);
        return false;
      }
    },
    async setAsDefault(card) {
      if (card.default) return;

      // Check if card is expired
      if (this.isExpired(card)) {
        this.error = true;
        this.errorMessage =
          "Cannot set an expired card as default. Please add a new card.";
        // Error messages persist until manually dismissed (per UX best practice)
        return;
      }

      this.error = false;
      this.errorMessage = "";
      this.successMessage = "";
      this.pendingDefaultCard = card;
      this.checkingSubscriptions = true;

      try {
        // Check if company has active subscriptions
        const response = await BillingService.hasActiveSubscriptions(this.$auth.activeCompany);
        this.hasSubscriptions = response.success && response.hasActiveSubscriptions;
        
        if (this.hasSubscriptions) {
          // Show modal to ask if they want to update subscriptions too
          this.$bvModal.show("change-default-modal");
        } else {
          // No subscriptions, just set default directly
          await this.executeSetDefault(card);
        }
      } catch (error) {
        // If check fails, just proceed with setting default
        await this.executeSetDefault(card);
      } finally {
        this.checkingSubscriptions = false;
      }
    },
    async executeSetDefault(card, alsoUpdateSubscriptions = false) {
      this.settingDefault = true;
      this.error = false;
      this.errorMessage = "";
      this.successMessage = "";

      try {
        const billingInfo = {
          company: this.$auth.activeCompany,
          cardInfo: {
            paymentProfileId: card.paymentProfileId,
          },
        };

        const result = await BillingService.setDefaultPaymentMethod(billingInfo);

        if (result) {
          if (alsoUpdateSubscriptions) {
            // Also update all subscriptions to use this card
            try {
              const response = await BillingService.updateAllSubscriptionsPaymentProfile(
                this.$auth.activeCompany,
                card.paymentProfileId
              );
              
              if (response.success) {
                const summary = response.summary;
                if (summary.failed === 0) {
                  this.successMessage = `Default updated and ${summary.successful} subscription(s) updated`;
                } else {
                  this.successMessage = `Default updated. ${summary.successful} subscription(s) updated, ${summary.failed} failed.`;
                }
              } else {
                this.successMessage = "Default updated, but failed to update subscriptions";
              }
            } catch (subError) {
              this.successMessage = "Default updated, but failed to update subscriptions";
            }
          } else {
            this.successMessage = "Default payment method updated";
          }
          
          TrackingService.track("set default payment card");
          await this.reloadCards();

          setTimeout(() => {
            this.successMessage = "";
          }, 6000);
        } else {
          throw new Error("Failed to set default payment method");
        }
      } catch (error) {
        this.error = true;
        // Extract the actual error message from the backend response
        if (error.response && error.response.data && error.response.data.message) {
          this.errorMessage = error.response.data.message;
        } else if (error.message) {
          this.errorMessage = error.message;
        } else {
          this.errorMessage = "Failed to set default payment method. Please try again.";
        }
      } finally {
        this.settingDefault = false;
        this.pendingDefaultCard = null;
      }
    },
    async handleChangeDefaultConfirm(updateSubscriptions) {
      this.$bvModal.hide("change-default-modal");
      if (this.pendingDefaultCard) {
        await this.executeSetDefault(this.pendingDefaultCard, updateSubscriptions);
      }
    },
    async updateSubscriptionsOnly(card) {
      // Update all subscriptions to use this card (without changing default)
      this.updatingSubscriptions = true;
      this.error = false;
      this.errorMessage = "";
      this.successMessage = "";

      try {
        const response = await BillingService.updateAllSubscriptionsPaymentProfile(
          this.$auth.activeCompany,
          card.paymentProfileId
        );
        
        if (response.success) {
          const summary = response.summary;
          if (summary.failed === 0) {
            this.successMessage = `${summary.successful} subscription(s) updated to use this card`;
          } else {
            this.successMessage = `${summary.successful} subscription(s) updated, ${summary.failed} failed`;
          }
          TrackingService.track("updated subscriptions payment profile");

          setTimeout(() => {
            this.successMessage = "";
          }, 6000);
        } else {
          throw new Error(response.message || "Failed to update subscriptions");
        }
      } catch (error) {
        this.error = true;
        if (error.response && error.response.data && error.response.data.message) {
          this.errorMessage = error.response.data.message;
        } else if (error.message) {
          this.errorMessage = error.message;
        } else {
          this.errorMessage = "Failed to update subscriptions. Please try again.";
        }
      } finally {
        this.updatingSubscriptions = false;
      }
    },
    async checkForSubscriptions() {
      try {
        const response = await BillingService.hasActiveSubscriptions(this.$auth.activeCompany);
        this.hasSubscriptions = response.success && response.hasActiveSubscriptions;
      } catch (error) {
        // If check fails, assume no subscriptions (button won't show)
        this.hasSubscriptions = false;
      }
    },
  },
  computed: {
    activePaymentMethods() {
      // Filter out expired cards - only show active cards
      return this.paymentMethods.filter(card => !this.isExpired(card));
    },
    allCardsExpired() {
      // Only show warning if ALL cards are expired (no active cards to display)
      return this.paymentMethods.length > 0 && this.activePaymentMethods.length === 0;
    },
  },
  async mounted() {
    await this.reloadCards();
    // Check for subscriptions so we know whether to show the "Update subscriptions" button
    await this.checkForSubscriptions();
  },
};
</script>
